import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useRequestVoiceIdentification } from '@utils/api/editors/voice-identifications';
import { Session } from '@utils/types/editors/sessions.ts';
import {
  formatRangeClock,
  formatSelectionDuration
} from '@utils/editors/voice-sample-ranges.ts';
import { useVoiceProfileFeature } from './use-voice-profile-feature.ts';
import { useVoiceIdentity } from './use-voice-identity.ts';
import { useVoiceSpeakerRow } from './use-voice-speaker-row.tsx';
import { useSpeakerNames } from './use-speaker-names.ts';
import { RecognitionStatus } from './recognition-status.tsx';

interface Props {
  session?: Session;
  documentId: string;
  readOnly?: boolean;
}

/** One row per detected voice, with one entry point.
 *
 * Grouped by original `speaker_label`, not by turn — one label has many turns,
 * and the decision applies to the label across the session. */
const VoiceLabelRow = ({
  sessionId,
  documentId,
  speakerLabel,
  startMs,
  stopMs,
  totalMs,
  feature,
  identity,
  resolveSpeakerName
}: {
  sessionId: string;
  documentId: string;
  speakerLabel: string;
  startMs: number;
  stopMs: number;
  totalMs: number;
  feature: ReturnType<typeof useVoiceProfileFeature>;
  identity: ReturnType<typeof useVoiceIdentity>;
  resolveSpeakerName: (id: number) => string | undefined;
}) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const row = useVoiceSpeakerRow({
    sessionId,
    documentId,
    startMs,
    stopMs,
    currentName: '',
    feature,
    identity,
    // The Speakers panel exists specifically to manage identity across the
    // recording — picks here commit a voice-wide backend decision, unlike
    // the editor row pickers which default to per-paragraph display only.
    defaultAssignScope: 'voice'
  });

  const identification = identity.byLabel.get(speakerLabel);
  const effectiveId = identification?.effective_speaker_id ?? null;
  const assignedName =
    effectiveId != null ? resolveSpeakerName(effectiveId) : undefined;

  const decision = () => {
    if (
      identification?.override_set &&
      identification.override_speaker_id === null
    )
      return t('voice-unknown');
    if (assignedName) return assignedName;
    return t('voice-not-assigned');
  };

  // One button, not two. "Choose" opens the picker; the enrollment follow-up
  // is offered via the toast after assignment (or by the dialog's own
  // chain-to-enroll mode when the admin starts from "Save voice sample").
  const primaryLabel = assignedName
    ? t('voice-change-person')
    : t('voice-choose-person');

  return (
    <li
      className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
      style={{ borderColor: getColour('border') }}
    >
      <div className="min-w-0 flex-1">
        <Text as="p" size="sm" weight="medium">
          {speakerLabel}
        </Text>
        <Text as="p" size="xs" tone="muted">
          {t('voice-label-timing', {
            first: formatRangeClock(startMs),
            total: formatSelectionDuration(totalMs)
          })}
        </Text>
        <Text as="p" size="xs">
          {decision()}
        </Text>
      </div>

      {row.chip}

      <div className="flex gap-1">
        {row.canAssign && (
          <Button
            variant="secondary"
            size="small"
            label={primaryLabel}
            onClick={() => row.openAssign()}
          />
        )}
        {/* Admins who already have a person linked can jump straight to
            enrollment — the person is kept, no re-pick. Without a person yet,
            the "Choose" button above opens the picker in chain-to-enroll mode
            if the admin presses it with intent to enroll, so we don't need a
            second entry point here. */}
        {row.canEnroll && assignedName && (
          <Button
            variant="secondary"
            size="small"
            label={t('voice-remember-this-voice')}
            onClick={row.openEnroll}
          />
        )}
      </div>

      {row.dialogs}
    </li>
  );
};

/** A compact Speakers panel for layouts with no speaker label of their own.
 *
 * Hidden entirely when the feature is off or the user may do nothing with it,
 * so no caption editor grows an empty panel. */
export const SpeakersVoicePanel = ({
  session,
  documentId,
  readOnly = false
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [expanded, setExpanded] = useState(false);

  const sessionId = session?.session_id ?? '';
  const feature = useVoiceProfileFeature({ session, readOnly });
  // The panel is collapsed by default, so it fetches nothing until opened —
  // but recognition suggestions are fetched eagerly even when collapsed, so
  // auto-apply of strong matches can run without the user opening the panel.
  const identity = useVoiceIdentity(
    sessionId,
    documentId,
    !!sessionId &&
      !!documentId &&
      feature.canAssign &&
      (feature.eagerIdentity || expanded)
  );

  const rerun = useRequestVoiceIdentification(sessionId, documentId);

  // Resolve every id the document references, once for the whole panel.
  const referencedIds = useMemo(() => {
    const ids: number[] = [];
    for (const row of identity.byLabel.values()) {
      if (row.suggested_speaker_id != null) ids.push(row.suggested_speaker_id);
      if (row.override_speaker_id != null) ids.push(row.override_speaker_id);
      if (row.effective_speaker_id != null) ids.push(row.effective_speaker_id);
    }
    return ids;
  }, [identity.byLabel]);

  const resolveSpeakerName = useSpeakerNames(
    referencedIds,
    feature.canAssign || feature.canReviewSuggestions
  );

  // Nothing to offer: don't render a panel at all.
  if (feature.isLoading) return null;
  if (!feature.canAssign && !feature.canReviewSuggestions) return null;

  return (
    <section
      className="rounded-lg border p-3"
      style={{ borderColor: getColour('border') }}
      aria-label={t('voice-speakers-panel')}
    >
      <button
        type="button"
        onClick={() => setExpanded(current => !current)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 text-left cursor-pointer"
      >
        {expanded ? (
          <ChevronDown size={14} aria-hidden />
        ) : (
          <ChevronRight size={14} aria-hidden />
        )}
        <Users size={14} aria-hidden />
        <Text as="span" size="sm" weight="medium">
          {t('voice-speakers-panel')}
        </Text>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* No source diarization: explain it instead of showing an empty
              selection flow the user cannot complete. */}
          {identity.noDiarization ? (
            <Text as="p" size="sm" tone="muted">
              {t('voice-error-no-diarization')}
            </Text>
          ) : (
            <>
              {feature.canReviewSuggestions && (
                <RecognitionStatus
                  status={identity.status}
                  reason={identity.statusReason}
                  canRerun={feature.sourceAvailable && feature.canAssign}
                  onRerun={() => rerun.mutate()}
                  isRerunning={rerun.isLoading}
                  error={rerun.error}
                />
              )}

              {/* The session has not been processed or retained: enrollment and
                  recognition cannot run, and saying so beats a dead button. */}
              {!feature.sourceAvailable && (
                <Text as="p" size="xs" tone="muted">
                  {t('voice-error-session-not-retained')}
                </Text>
              )}

              <ul className="space-y-2">
                {identity.labels.map(label => (
                  <VoiceLabelRow
                    key={label.speaker_label}
                    sessionId={sessionId}
                    documentId={documentId}
                    speakerLabel={label.speaker_label}
                    startMs={label.firstStartMs}
                    stopMs={label.lastStopMs}
                    totalMs={label.totalMs}
                    feature={feature}
                    identity={identity}
                    resolveSpeakerName={resolveSpeakerName}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
};
